import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { KeyValue } from '@magnetic/key-value'
import { Toggle } from '@magnetic/switch'
import { GitBranch, FileCode } from '@/lib/icons'
import { deriveSyncHealthFromState, getHealthIcon, getSyncIcon } from '@/utils/applicationStatus'
import { formatTimestamp } from '@/lib/protobuf-utils'
import type { Application as ProtoApplication, SyncPolicy as ProtoSyncPolicy } from '@/lib/generated-types'

type Application = Pick<ProtoApplication, 'repoUrl' | 'path' | 'targetRevision' | 'state' | 'stateMessage' | 'lastSyncTime'>
type SyncPolicy = Pick<ProtoSyncPolicy, 'autoSync' | 'selfHeal' | 'pruneResources'>

interface StatusCardsProps {
  application: Application
  syncPolicy: SyncPolicy
  onUpdateSyncPolicy: (policy: SyncPolicy) => void
  isPending: boolean
}

export function StatusCards({
  application,
  syncPolicy,
  onUpdateSyncPolicy,
  isPending,
}: StatusCardsProps) {
  const { healthStatus, syncStatus } = deriveSyncHealthFromState(application.state)
  const statusMessage = application.stateMessage || ''
  const lastSyncTime = application.lastSyncTime

  return (
    <Card.Group grow>
      <Card style={{ flex: '1 1 300px', boxShadow: 'none' }}>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Heading size="sub-section">Status</Heading>
          <Text size="p4" color="light">{statusMessage || 'No status message'}</Text>
          <KeyValue>
            <KeyValue.Pair
              pair={[
                'Health',
                <Flex align="center" gap={8}>
                  {getHealthIcon(healthStatus)}
                  <Text size="p3" weight="bold">{healthStatus}</Text>
                </Flex>,
              ]}
            />
            <KeyValue.Pair
              pair={[
                'Sync status',
                <Flex align="center" gap={8}>
                  {getSyncIcon(syncStatus)}
                  <Text size="p3" weight="bold">{syncStatus}</Text>
                </Flex>,
              ]}
            />
            <KeyValue.Pair
              pair={[
                'Last sync',
                lastSyncTime ? formatTimestamp(lastSyncTime) : 'N/A',
              ]}
            />
          </KeyValue>
        </Flex>
      </Card>

      <Card style={{ flex: '1 1 300px', boxShadow: 'none' }}>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <GitBranch size={20} />
            <Heading size="sub-section">Source</Heading>
          </Flex>
          <KeyValue>
            <KeyValue.Pair
              pair={[
                'Repository',
                application.repoUrl ? (
                  <a href={application.repoUrl} target="_blank" rel="noopener noreferrer">
                    {application.repoUrl.replace('https://github.com/', '')}
                  </a>
                ) : 'N/A',
              ]}
            />
            <KeyValue.Pair
              pair={[
                'Path',
                application.path ? <code>{application.path}</code> : 'N/A',
              ]}
            />
            <KeyValue.Pair
              pair={[
                'Target revision',
                <code>{application.targetRevision || 'main'}</code>,
              ]}
            />
          </KeyValue>
        </Flex>
      </Card>

      <Card style={{ flex: '1 1 300px', boxShadow: 'none' }}>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <FileCode size={20} />
            <Heading size="sub-section">Sync Policy</Heading>
          </Flex>
          <Flex direction="vertical" gap="md">
            <Flex align="center" justify="space-between">
              <Flex direction="vertical" gap={4}>
                <Text size="p3" weight="semi-bold">Auto sync</Text>
                <Text size="p4" color="light">Automatically sync changes</Text>
              </Flex>
              <Toggle
                checked={syncPolicy.autoSync}
                onClick={() => {
                  onUpdateSyncPolicy({
                    autoSync: !syncPolicy.autoSync,
                    selfHeal: syncPolicy.selfHeal,
                    pruneResources: syncPolicy.pruneResources,
                  })
                }}
                disabled={isPending}
              />
            </Flex>
            <Flex align="center" justify="space-between">
              <Flex direction="vertical" gap={4}>
                <Text size="p3" weight="semi-bold">Self heal</Text>
                <Text size="p4" color="light">Revert drift automatically</Text>
              </Flex>
              <Toggle
                checked={syncPolicy.selfHeal}
                onClick={() => {
                  onUpdateSyncPolicy({
                    autoSync: syncPolicy.autoSync,
                    selfHeal: !syncPolicy.selfHeal,
                    pruneResources: syncPolicy.pruneResources,
                  })
                }}
                disabled={!syncPolicy.autoSync || isPending}
              />
            </Flex>
            <Flex align="center" justify="space-between">
              <Flex direction="vertical" gap={4}>
                <Text size="p3" weight="semi-bold">Prune resources</Text>
                <Text size="p4" color="light">Delete stale resources</Text>
              </Flex>
              <Toggle
                checked={syncPolicy.pruneResources}
                onClick={() => {
                  onUpdateSyncPolicy({
                    autoSync: syncPolicy.autoSync,
                    selfHeal: syncPolicy.selfHeal,
                    pruneResources: !syncPolicy.pruneResources,
                  })
                }}
                disabled={!syncPolicy.autoSync || isPending}
              />
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </Card.Group>
  )
}
